import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Alert, Box, Button, CircularProgress, IconButton, InputAdornment, Paper, Stack, TextField, Typography,
} from '@mui/material';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import DefaultLogo from '../../assets/images/smart-stable-default-logo.png';
import SmartStableLogo from '../../assets/images/SmartStable.png';
import api from '../../services/api';
import { APP_VERSION_LABEL } from '../../version';
import { SMART_COLORS as C } from '../../theme/smartStableTheme';

const Login = ({ setIsAuthenticated }) => {
  const { t } = useTranslation();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [brand, setBrand] = useState({ name: '', logo: null });
  const navigate = useNavigate();

  // العلامة (الاسم + الشعار القابل للتخصيص) من نقطة عامة — تتيح تخصيص اللوجو من الإعدادات.
  useEffect(() => {
    api.get('/system-name')
      .then(({ data }) => setBrand({ name: data?.system_name || '', logo: data?.logo || null }))
      .catch(() => {});
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const response = await api.post('/login', { username, password });
      if (response.data) {
        localStorage.setItem('token', response.data.token);
        if (response.data.refresh_token) localStorage.setItem('refresh_token', response.data.refresh_token);
        localStorage.setItem('userName', response.data.userName);
        localStorage.setItem('userId', response.data.userId);
        if (response.data.owner_id) localStorage.setItem('owner_id', response.data.owner_id);
        else localStorage.removeItem('owner_id');
        localStorage.setItem('role_id', response.data.role_id);
        localStorage.setItem('is_system_admin', Number(response.data.is_system_admin || 0));
        if (response.data.permissions) localStorage.setItem('permissions', JSON.stringify(response.data.permissions));
        else localStorage.removeItem('permissions');
        setIsAuthenticated(true);
        navigate('/home');
      }
    } catch (err) {
      setError(t('invalid_credentials'));
    } finally {
      setLoading(false);
    }
  };

  const logoSrc = brand.logo || DefaultLogo;

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        p: 2,
        position: 'relative',
        background: `linear-gradient(135deg, ${C.appbar} 0%, ${C.sidebarAccent} 100%)`,
        '&::before': {
          content: '""',
          position: 'absolute',
          inset: 0,
          background: 'radial-gradient(1200px 600px at 50% -10%, rgba(255,255,255,0.14), transparent 60%)',
          pointerEvents: 'none',
        },
      }}
    >
      <Paper
        elevation={0}
        sx={{
          width: '100%',
          maxWidth: 430,
          p: { xs: 3, sm: 4.5 },
          borderRadius: 4,
          bgcolor: C.card,
          border: `1px solid ${C.line}`,
          boxShadow: '0 24px 60px rgba(0,0,0,0.28)',
          position: 'relative',
          zIndex: 1,
        }}
      >
        {/* الشعار + اسم النظام */}
        <Stack alignItems="center" spacing={1.5} sx={{ mb: 3 }}>
          <Box
            sx={{
              width: 132, height: 132,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}
          >
            {/* بلا أي تأثير (زوايا/ظلّ) — يُعرض الشعار المرفوع كما هو دون تقييد المستخدم. */}
            <img
              src={logoSrc}
              alt="Logo"
              style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
            />
          </Box>
          {brand.name && (
            <Typography variant="h5" sx={{ fontWeight: 900, color: C.text, textAlign: 'center' }}>
              {brand.name}
            </Typography>
          )}
          <Stack direction="row" spacing={0.75} alignItems="center" sx={{ color: C.muted }}>
            <LockOutlinedIcon sx={{ fontSize: 18 }} />
            <Typography variant="body2" sx={{ fontWeight: 700 }}>{t('login', 'تسجيل الدخول')}</Typography>
          </Stack>
        </Stack>

        <Box component="form" onSubmit={handleSubmit} noValidate>
          <TextField
            margin="normal" required fullWidth id="username" label={t('username')} name="username"
            autoComplete="username" autoFocus value={username} onChange={(e) => setUsername(e.target.value)}
          />
          <TextField
            margin="normal" required fullWidth name="password" label={t('password')}
            type={showPassword ? 'text' : 'password'} id="password" autoComplete="current-password"
            value={password} onChange={(e) => setPassword(e.target.value)}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton aria-label="toggle password visibility"
                    onClick={() => setShowPassword((s) => !s)}
                    onMouseDown={(ev) => ev.preventDefault()} edge="end">
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
          {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
          <Button
            type="submit" fullWidth variant="contained" disabled={loading}
            sx={{
              mt: 3, mb: 1, py: 1.25, fontWeight: 900, fontSize: 16, borderRadius: 2,
              bgcolor: C.appbar, color: '#fff',
              '&:hover': { bgcolor: C.appbarHover },
            }}
          >
            {loading ? <CircularProgress size={24} sx={{ color: '#fff' }} /> : t('login', 'تسجيل الدخول')}
          </Button>
        </Box>

        {/* تذييل: تطوير Smart Stable + الإصدار */}
        <Stack alignItems="center" spacing={0.5} sx={{ mt: 3, pt: 2.5, borderTop: `1px solid ${C.line}` }}>
          <img src={SmartStableLogo} alt="Smart Stable" style={{ width: 46, opacity: 0.85 }} />
          <Typography variant="caption" sx={{ color: C.muted, textAlign: 'center' }}>
            Powered by <strong>Smart Stable</strong>
          </Typography>
          <Typography variant="caption" sx={{ color: C.muted, fontWeight: 700, letterSpacing: 0.5 }}>
            © {new Date().getFullYear()} · {APP_VERSION_LABEL}
          </Typography>
        </Stack>
      </Paper>
    </Box>
  );
};

export default Login;
